import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, CheckCircle2, TrendingUp, Calendar as CalendarIcon, Lock } from 'lucide-react';
import { API, photoUrl } from '../lib/api';

const SoldVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [societeFilter, setSocieteFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchSocietes();
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [societeFilter, typeFilter, search]);

  const fetchSocietes = async () => {
    try {
      const res = await axios.get(`${API}/societes`);
      setSocietes(res.data);
    } catch (error) {
      console.error('Error fetching societes:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const params = new URLSearchParams();
      params.append('statut', 'vendu');
      if (societeFilter) params.append('societe_id', societeFilter);
      if (typeFilter) params.append('type_vehicule', typeFilter);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/vehicles?${params.toString()}`);
      setVehicles(res.data);
    } catch (error) {
      console.error('Error fetching sold vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  // Statistiques calculées
  const totalCA = vehicles.reduce((acc, v) => acc + (v.prix_vente || 0), 0);
  const totalMarge = vehicles.reduce((acc, v) => acc + ((v.prix_vente || 0) - (v.prix_achat || 0)), 0);

  const renderVehicleCard = (vehicle) => {
    const marge = (vehicle.prix_vente || 0) - (vehicle.prix_achat || 0);
    const margeColor = marge >= 0 ? 'text-green-600' : 'text-red-600';
    return (
      <Link
        key={vehicle.id}
        to={`/vehicles/${vehicle.id}`}
        data-testid={`sold-vehicle-card-${vehicle.immatriculation}`}
        className="vehicle-card overflow-hidden"
      >
        <div className="relative h-48 bg-slate-200 overflow-hidden">
          {vehicle.photos && vehicle.photos[0] ? (
            <img
              src={photoUrl(vehicle.photos[0])}
              alt={`${vehicle.marque} ${vehicle.modele}`}
              className="w-full h-full object-cover opacity-90"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <span>Pas de photo</span>
            </div>
          )}
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 bg-slate-900 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
              <CheckCircle2 size={12} />
              Vendu
            </span>
          </div>
        </div>

        <div className="p-5">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-semibold text-lg text-slate-900">{vehicle.marque} {vehicle.modele}</h3>
              <p className="text-sm text-slate-600 font-mono">{vehicle.immatriculation}</p>
              {vehicle.type_vehicule === 'utilitaire' && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                  Utilitaire
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Année</span>
              <span className="font-mono text-slate-900">{vehicle.annee}</span>
            </div>
            {vehicle.prix_vente && (
              <div className="flex justify-between">
                <span className="text-slate-600">Prix vente TTC</span>
                <span className="font-mono text-green-600 font-semibold">{vehicle.prix_vente.toLocaleString('fr-FR')} €</span>
              </div>
            )}
            <div className="flex justify-between pt-2 mt-2 border-t border-slate-100">
              <span className="text-slate-500 text-xs flex items-center gap-1">
                <Lock size={11} /> Marge
              </span>
              <span className={`font-mono text-xs font-semibold ${margeColor}`}>
                {marge >= 0 ? '+' : ''}{marge.toLocaleString('fr-FR')} €
              </span>
            </div>
            {vehicle.date_vente && (
              <div className="flex justify-between">
                <span className="text-slate-600 text-xs flex items-center gap-1">
                  <CalendarIcon size={11} /> Vendu le
                </span>
                <span className="font-mono text-slate-700 text-xs">
                  {new Date(vehicle.date_vente).toLocaleDateString('fr-FR')}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div data-testid="sold-vehicles-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="text-green-400" size={22} />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Véhicules vendus
            </h2>
          </div>
          <p className="text-slate-600">
            {vehicles.length} véhicule(s) vendu(s) — historique privé (non visible des clients)
          </p>
        </div>
      </div>

      {/* Stats récap */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div data-testid="stat-ca" className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
            <TrendingUp className="text-green-600" size={22} />
          </div>
          <div>
            <p className="text-xs text-slate-500">Chiffre d'affaires (TTC)</p>
            <p className="text-2xl font-bold text-slate-900 font-mono">
              {totalCA.toLocaleString('fr-FR')} €
            </p>
          </div>
        </div>
        <div data-testid="stat-marge" className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
            <Lock className="text-slate-600" size={22} />
          </div>
          <div>
            <p className="text-xs text-slate-500">Marge brute totale (privé)</p>
            <p className={`text-2xl font-bold font-mono ${totalMarge >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {totalMarge >= 0 ? '+' : ''}{totalMarge.toLocaleString('fr-FR')} €
            </p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                data-testid="sold-search-input"
                placeholder="Rechercher par immatriculation, marque, modèle..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              data-testid="sold-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Tous les types</option>
              <option value="voiture">Voitures</option>
              <option value="utilitaire">Utilitaires</option>
            </select>

            <select
              data-testid="sold-societe-filter"
              value={societeFilter}
              onChange={(e) => setSocieteFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Toutes les sociétés</option>
              {societes.map(societe => (
                <option key={societe.id} value={societe.id}>{societe.nom}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {vehicles.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
          <CheckCircle2 className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500 mb-2">Aucun véhicule vendu pour le moment</p>
          <p className="text-slate-400 text-sm">
            Les véhicules apparaîtront ici dès que leur statut passera à "Vendu".
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map(vehicle => renderVehicleCard(vehicle))}
        </div>
      )}
    </div>
  );
};

export default SoldVehicles;
